import map from 'lodash/map';
import every from 'lodash/every';
import zipObject from 'lodash/zipObject';
import { createAsyncThunk } from '@reduxjs/toolkit';
import departmentRequestsApi from '../../../api/departmentRequestsApi';
import summaryDepartmentRequestsApi from '../../../api/summaryDepartmentRequestsApi';
import { SummaryDepartmentRequest } from '../../../types/departmentRequest';
import productTemplatesApi from '../../../api/productTemplatesApi';
import productBrandsApi from '../../../api/productBrandsApi';
import rawMaterialInventoryApi from '../../../api/rawMaterialInventoryApi';
import inventoryApi from '../../../api/workInProgressInventoryApi';
import workOrderApi from '../../../api/workOrderApi';
import employeeTeamsApi from '../../../api/employeeTeamsApi';
import factoryOptionsApi from '../../../api/factoryOptionsApi';
import { EmployeeTeamDepartmentEnum, FactoryOptionNameEnum, InventoryItemTypeEnum } from '../../../constants/enums';
import { RawMaterialPurposeEnum } from '../../../constants/rawMaterialsInventory';
import { SetAlertAndClose } from '../AlertReducer';

// ---------------------------------------------------------------------

const REDUCER_NAME = 'departmentRequest';

export const CreateDepartmentRequest = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/createDepartmentRequest`,
  async (data, thunkAPI) => {
    const res = await departmentRequestsApi.create(data);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetDepartmentRequestById = createAsyncThunk<any, string>(
  `${REDUCER_NAME}/getDepartmentRequestById`,
  async (id, thunkAPI) => {
    const res = await departmentRequestsApi.getById(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetDepartmentRequests = createAsyncThunk(`${REDUCER_NAME}/getDepartmentRequests`, async (__, thunkAPI) => {
  const res = await departmentRequestsApi.get();

  if (res.status === 200) {
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const UpdateDepartmentRequest = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/updateDepartmentRequest`,
  async ({ data, id }, thunkAPI) => {
    const res = await departmentRequestsApi.update(data, id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const CreateWorkOrder = createAsyncThunk<any, any>(`${REDUCER_NAME}/createWorkOrder`, async (data, thunkAPI) => {
  const res = await workOrderApi.create(data);

  if (res.status === 200) {
    thunkAPI.dispatch(SetAlertAndClose({ data: res.data }));
    return res.data;
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const DeleteDepartmentRequest = createAsyncThunk<any, string>(
  `${REDUCER_NAME}/deleteDepartmentRequest`,
  async (id, thunkAPI) => {
    const res = await departmentRequestsApi.delete(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetDepartmentRequestInventoryCigars = createAsyncThunk(
  `${REDUCER_NAME}/getDepartmentRequestInventoryCigars`,
  async (__, thunkAPI) => {
    const res = await inventoryApi.getProductionReadyStockItems({ type: InventoryItemTypeEnum.Cigar });

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetDepartmentRequestRawMaterials = createAsyncThunk(
  `${REDUCER_NAME}/getDepartmentRequestRawMaterials`,
  async (__, thunkAPI) => {
    const res = await rawMaterialInventoryApi.get({ purpose: RawMaterialPurposeEnum.Packing });

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetDepartmentRequestProductTemplates = createAsyncThunk(
  `${REDUCER_NAME}/getDepartmentRequestProductTemplates`,
  async (__, thunkAPI) => {
    const res = await productTemplatesApi.get();

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetDepartmentRequestProductBrands = createAsyncThunk(
  `${REDUCER_NAME}/getDepartmentRequestProductBrands`,
  async (__, thunkAPI) => {
    const res = await productBrandsApi.get();

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const CreateProductBrand = createAsyncThunk<{ name: string; data: any }, any>(
  `${REDUCER_NAME}/createProductBrand`,
  async (data, thunkAPI) => {
    const res = await productBrandsApi.create(data);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const MarkDepartmentRequestDone = createAsyncThunk(
  `${REDUCER_NAME}/markDepartmentRequestDone`,
  async (id, thunkAPI) => {
    const res = await departmentRequestsApi.markDone(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const MarkDepartmentRequestPending = createAsyncThunk<any, string>(
  `${REDUCER_NAME}/markDepartmentRequestPending`,
  async (id, thunkAPI) => {
    const res = await departmentRequestsApi.markPending(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const MarkDepartmentRequestTaskDone = createAsyncThunk<any, string>(
  `${REDUCER_NAME}/markDepartmentRequestTaskDone`,
  async (id, thunkAPI) => {
    const res = await departmentRequestsApi.markTaskDone(id);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetPackingDepartmentEmployeeTeams = createAsyncThunk(
  `${REDUCER_NAME}/getPackingDepartmentEmployeeTeams`,
  async (__, thunkAPI) => {
    const res = await employeeTeamsApi.get({ department: EmployeeTeamDepartmentEnum.Packing });

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetFactoryOptions = createAsyncThunk(`${REDUCER_NAME}/getFactoryOptions`, async (__, thunkAPI) => {
  const optionNames = [FactoryOptionNameEnum.FinishedGoodTypes];

  const optionResponses = await Promise.all(map(optionNames, (name) => factoryOptionsApi.getByName(name)));

  const allResponsesSuccessful = every(optionResponses, (res) => res.status === 200);

  if (allResponsesSuccessful) {
    const data = map(optionResponses, 'data');
    return zipObject(optionNames, data);
  }

  return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
});

export const CreateFactoryOption = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/createFactoryOption`,
  async (data, thunkAPI) => {
    const res = await factoryOptionsApi.create(data);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetSummaryDepartmentRequestByDepartmentRequestId = createAsyncThunk<SummaryDepartmentRequest, string>(
  `${REDUCER_NAME}/getSummaryDepartmentRequestByDepartmentRequestId`,
  async (id, thunkAPI) => {
    const res = await summaryDepartmentRequestsApi.getByDepartmentRequestId(id);

    if (res.status === 200) {
      return res.data.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);

export const GetEditFormOptions = createAsyncThunk<any, any>(
  `${REDUCER_NAME}/getEditFormOptions`,
  async (optionName, thunkAPI) => {
    const res = await factoryOptionsApi.getByFactoryConfigOptionValueName(optionName);

    if (res.status === 200) {
      return res.data;
    }

    return thunkAPI.rejectWithValue({ status: 'error', message: 'Fetch failed' });
  }
);
